import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import ReviewProofStrip from "@/components/ReviewProofStrip";
import { GYM_PORTFOLIO, GYM_PORTFOLIO_FOOTNOTE } from "@/data/gymPortfolio";
import { GYM_MARKETING_FAQS } from "@/seo/pageMeta";
import {
  Dumbbell, MapPin, Star, Target, MonitorSmartphone,
  ArrowRight, Calendar, Instagram, Smartphone, FileText, Download,
} from "lucide-react";

const SERVICES = [
  { icon: MapPin, title: "SEO for Gyms", desc: "Rank for 'gym near me' searches and own the Google Map Pack in your city.", link: "/gym-seo" },
  { icon: MonitorSmartphone, title: "Gym Website Design", desc: "Conversion websites that launch with SEO, schema and lead capture wired in.", link: "/gym-website-design" },
  { icon: Target, title: "PPC & Paid Ads", desc: "Geo-targeted Google and Meta campaigns built to book trials, not clicks." },
  { icon: Instagram, title: "Social Media Marketing", desc: "Build an engaged fitness community on Instagram and TikTok." },
  { icon: Star, title: "Reputation Management", desc: "Generate 5-star reviews and member success stories." },
  { icon: Smartphone, title: "Retention Marketing", desc: "Reduce churn with email and SMS campaigns." },
];

const NICHES = [
  { h3: "CrossFit Marketing", text: "CrossFit members search by neighborhood, not city. We build box websites around WOD programming, coaching credentials and hyper-local keywords — CrossFit Boston Iron & Grit ranks #1 for \"crossfit west roxbury\" with a site that launched at zero." },
  { h3: "Pilates Studio Marketing", text: "Pilates clients research pricing and class styles before booking. We rank studios for those exact searches — Reyna Pilates hit #3 for \"pilates odessa tx\" before the studio even opened its doors." },
  { h3: "Yoga Studio Marketing", text: "Yoga studios win on schedule visibility, teacher pages and intro-offer funnels. We structure sites so every class type and style ranks on its own page and feeds your booking system." },
  { h3: "Martial Arts Marketing", text: "Parents searching for kids' classes and adults searching for self-defense are two different funnels. We build martial arts sites that capture both, with program pages and trial-class booking built in." },
  { h3: "Personal Trainer Marketing", text: "Personal trainers live and die on local search and proof. We build trainer sites around results, specialties and service-area pages so you show up when someone searches for a trainer near them." },
  { h3: "Boutique Fitness Marketing", text: "HIIT, cycling and boutique studios need launch momentum. PitFit's site grew top-10 rankings 71% in the first month after launch while its trial-offer funnel captured every inquiry." },
  { h3: "Health Club Marketing", text: "Multi-amenity clubs compete on everything from pools to pilates. Delta Valley Health Club went from 3 to 650 ranking keywords in five months by ranking each amenity and program on its own page." },
  { h3: "HYROX & Functional Fitness Marketing", text: "HYROX is the fastest-growing race format in fitness, and gyms that publish HYROX training content early own the rankings. Dallas Performance Systems already ranks for \"hyrox dallas\" (12,100 searches/mo).", link: "/hyrox-content", linkText: "See our HYROX content services →" },
];

const STEPS = [
  { value: "Leads", label: "Local SEO & Google Ads", color: "text-[#00FF85]" },
  { value: "Growth", label: "Member Acquisition Funnels", color: "text-blue-400" },
  { value: "Trials", label: "Conversion-Focused Pages", color: "text-purple-400" },
  { value: "Retention", label: "Email & SMS Win-Backs", color: "text-red-400" },
];

const RESULTS_ROWS = GYM_PORTFOLIO.slice(0, 6);

const GymMarketingPage = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="gym-marketing-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00FF85]/10 via-transparent to-purple-500/10"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00FF85]/10 border border-[#00FF85]/30 rounded-full mb-6">
              <Dumbbell className="w-4 h-4 text-[#00FF85]" />
              <span className="text-[#00FF85] text-sm font-medium">Fitness Industry Specialists</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="gym-marketing-h1">
              Gym Marketing Agency for <span className="text-[#00FF85]">Gyms, Studios & CrossFit Boxes</span>
            </h1>

            <p className="text-lg text-gray-300 mb-8 max-w-3xl" data-testid="gym-marketing-intro">
              Digital Media Ninja is a Dallas-based gym marketing agency that builds, ranks and advertises fitness businesses nationwide. We combine local SEO, Google and Meta ads, and conversion-focused websites. Across our gym clients, sites we built now draw 5,000+ estimated monthly organic visits (Semrush, Oct 2026).
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/contact?topic=gym">
                <Button size="lg" className="bg-[#00FF85] hover:bg-[#00FF85]/90 text-black font-semibold" data-testid="gym-hero-cta">
                  Get a Free Gym Growth Plan <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/blog/how-to-market-a-gym">
                <Button size="lg" variant="outline" className="border-[#00FF85]/30 text-[#00FF85] hover:bg-[#00FF85]/10" data-testid="read-blog-btn">
                  <FileText className="w-5 h-5 mr-2" /> How to Market a Gym
                </Button>
              </Link>
              <Link to="/guides/gym-marketing">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="read-playbook-btn">
                  <Download className="w-5 h-5 mr-2" /> Marketing Playbook
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 1. What we do */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Digital Marketing for Gyms: What We Do</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Comprehensive digital marketing tailored for fitness businesses.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gym-services-grid">
            {SERVICES.map((svc, i) => {
              const inner = (
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#00FF85]/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-[#00FF85]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                  {svc.link && <span className="inline-flex items-center gap-1 text-[#00FF85] text-sm mt-3">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>}
                </CardContent>
              );
              return svc.link ? (
                <Link key={i} to={svc.link} data-testid={`gym-service-card-${i}`}>
                  <Card className="bg-[#111] border-white/10 hover:border-[#00FF85]/40 transition-all h-full">{inner}</Card>
                </Link>
              ) : (
                <Card key={i} className="bg-[#111] border-white/10 hover:border-[#00FF85]/30 transition-all">{inner}</Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2. Gym advertising */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom max-w-4xl">
          <h2 className="text-3xl font-bold text-white mb-6">Gym Advertising: Google Ads & Meta Ads for Gyms</h2>
          <div className="text-gray-300 space-y-4 leading-relaxed" data-testid="gym-advertising-copy">
            <p>
              Gym ads only work when they're built around one thing: getting someone through your door for a trial. We build gym advertising campaigns around trial offers — free week passes, intro classes, founding-member rates — because that's the offer people searching "gym near me" actually respond to.
            </p>
            <p>
              Every campaign is geo-targeted to a radius your members actually drive from, usually 3–5 miles, so you never pay for clicks from people who will never visit. Call tracking and form tracking run on every lead, which means you see exactly which ad, keyword and offer produced each trial booking — not just traffic numbers.
            </p>
            <p>
              We then retarget by inquiry type: someone who looked at personal training sees trainer ads, someone who checked class schedules sees class-pass offers. Google Ads capture high-intent searches; Meta ads build demand and bring back visitors who didn't convert the first time.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Results table */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-10">Gym Marketing Results (Semrush-Verified)</h2>
          <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="gym-results-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-white/5 text-left">
                  <th className="px-5 py-4 text-gray-400 font-semibold">Gym</th>
                  <th className="px-5 py-4 text-gray-400 font-semibold">Before</th>
                  <th className="px-5 py-4 text-gray-400 font-semibold">After (Oct 2026)</th>
                  <th className="px-5 py-4 text-gray-400 font-semibold">Headline result</th>
                </tr>
              </thead>
              <tbody>
                {RESULTS_ROWS.map((row) => (
                  <tr key={row.slug} className="border-t border-white/5">
                    <td className="px-5 py-4">
                      <a href={`/gym-website-design#${row.slug}`} className="text-white font-semibold hover:text-[#00FF85]" data-testid={`gym-results-row-${row.slug}`}>{row.name}</a>
                      <p className="text-gray-500 text-xs mt-0.5">{row.type}{row.note ? ` · ${row.note}` : ` · ${row.city}`}</p>
                    </td>
                    <td className="px-5 py-4 text-gray-400">{row.beforeLabel}: {row.tableBefore}</td>
                    <td className="px-5 py-4 text-[#00FF85] font-medium">{row.tableAfter}</td>
                    <td className="px-5 py-4 text-gray-300">{row.headline}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-gray-600 text-xs mt-4">{GYM_PORTFOLIO_FOOTNOTE}</p>
          <div className="text-center mt-8">
            <Link to="/gym-website-design" className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline" data-testid="see-every-gym-site-link">
              See every gym site we've built <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Fitness businesses we market */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Fitness Businesses We Market</h2>
          <div className="grid md:grid-cols-2 gap-6" data-testid="gym-niches-grid">
            {NICHES.map((n) => (
              <div key={n.h3} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-[#00FF85]/30 transition-colors">
                <h3 className="text-white font-semibold text-lg mb-2">{n.h3}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{n.text}</p>
                {n.link && (
                  <Link to={n.link} className="inline-block text-[#00FF85] text-sm mt-3 hover:underline" data-testid="hyrox-content-link">{n.linkText}</Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. How we grow gym memberships */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">How We Grow Gym Memberships</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.value} className="text-center p-8 bg-[#111] rounded-2xl border border-white/10">
                <p className={`text-4xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-gray-400 mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Reviews */}
      <ReviewProofStrip />

      {/* 7. FAQ */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Gym Marketing FAQs</h2>
          <div className="max-w-3xl mx-auto space-y-4" data-testid="gym-marketing-faq">
            {GYM_MARKETING_FAQS.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10">
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5" data-testid={`gym-faq-${i}`}>
                  {faq.q}
                  <span className="text-[#00FF85] group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 8. CTA band */}
      <section className="py-20 bg-gradient-to-r from-[#00FF85]/10 to-purple-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Get a Free Gym Growth Plan</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">A free 20-minute strategy call and a written 90-day plan for your gym. Month to month, no contract.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact?topic=gym">
              <Button size="lg" className="bg-[#00FF85] hover:bg-[#00FF85]/90 text-black font-semibold" data-testid="gym-cta-band-btn">
                <Calendar className="w-5 h-5 mr-2" /> Get My Free Growth Plan
              </Button>
            </Link>
            <Link to="/guides/gym-marketing">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                View Interactive Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GymMarketingPage;
